import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationStatusEnumType,
  InvestmentEntityDocumentTypeEnumType,
} from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import accreditationStatusToReadable from 'utils/enums/accreditation-status-to-readable';
import fundingSourceToReadable from 'utils/enums/funding-source-to-readable';
import statusEnumToReadable from 'utils/enums/status-enum-to-readable';

import ClosingSlideOver from './ClosingSlideOver';
import CreateClosingDialogButton from './CreateClosingDialogButton';

const CLOSINGS_QUERY = gql(`
  query fetchClosingsByDeal($dealId: ID!, $cursor: ID, $limit: Int, $filters: ClosingsFilterType) {
    deal(id: $dealId) {
      id
      syndicate {
        type
      }
      closings(cursor: $cursor, limit: $limit, filters: $filters) {
        nodes {
          id
          amount
          status
          investmentEntity {
            id
            name
            user {
              id
              name
            }
            investorApplication {
              id
              accreditationStatus
            }
            investmentEntityDocuments(statuses: [ACTIVE, DRAFT]) {
              id
              type
              signingStatus
            }
          }
          fundDealClosingParticipants {
            id
            status
            amountInvested
            ownershipPercentage
            optedInAt
            investmentEntity {
              id
              name
              user {
                id
                name
              }
            }
          }
          fundingSource
          ownershipPercentage
          currentValue
          realizedValue
          alCarryPercentage
          gpCarryPercentage
          createdAt
          updatedAt
          sourceDealInviteLink {
            id
          }
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
      statistics {
        dealStatisticsBreakdownByClosingStatus {
          status
          numberOfClosings
        }
        dealStatisticsBreakdownByClosingFundingSource {
          fundingSource
          numberOfClosings
        }
      }
    }
  }
`);

const DealClosings: FC = () => {
  const { dealId } = useParams<{ dealId: string }>() as { dealId: string };
  const [closingOnDisplay, setClosingOnDisplay] = useState('');

  const { loading, error, data, refetch, fetchMore, variables } = useQuery(CLOSINGS_QUERY, {
    variables: {
      limit: 50,
      dealId,
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const deal = data.deal;
  const closings = deal.closings.nodes.map(closing => ({
    ...closing,
    _contributionAgreementSigningStatus: closing.investmentEntity?.investmentEntityDocuments?.find(
      doc => doc.type === InvestmentEntityDocumentTypeEnumType.ContributionAgreement
    )?.signingStatus,
    _investorNameWithBadge: (() => {
      const investorName = closing.investmentEntity?.user?.name || '-';
      const accreditationStatus =
        closing.investmentEntity?.investorApplication?.accreditationStatus;
      const isAccredited = accreditationStatus === AccreditationStatusEnumType.Accredited;

      return (
        <span>
          {investorName}
          {isAccredited && (
            <span className="ml-1 inline-flex">
              <Badge label="AI" color={accreditationStatusToReadable(accreditationStatus).color} />
            </span>
          )}
        </span>
      );
    })(),
  }));
  const pageInfo = deal.closings.pageInfo;
  const statistics = deal.statistics;

  const filters = [
    {
      type: 'statuses',
      label: 'Status',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...statistics.dealStatisticsBreakdownByClosingStatus.map(
          ({ status, numberOfClosings }) => ({
            label: `${statusEnumToReadable(status).label} (${numberOfClosings})`,
            value: [status],
          })
        ),
      ],
      readable: statusEnumToReadable,
    },
    {
      type: 'fundingSource',
      label: 'Funding Source',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...statistics.dealStatisticsBreakdownByClosingFundingSource.map(
          ({ fundingSource, numberOfClosings }) => ({
            label: `${fundingSourceToReadable(fundingSource).label} (${numberOfClosings})`,
            value: fundingSource,
          })
        ),
      ],
      readable: fundingSourceToReadable,
    },
  ];

  return (
    <>
      <ClosingSlideOver
        open={!!closingOnDisplay}
        closingId={closingOnDisplay}
        onClose={() => setClosingOnDisplay('')}
      />
      <div>
        <CreateClosingDialogButton dealId={deal.id} />
      </div>
      <FilterPanel activeFilters={variables?.filters} filters={filters} refetch={refetch} />
      <DataTable
        data={closings}
        searchFields={['investmentEntity.name', 'investmentEntity.user.name']}
        onClick={closing => setClosingOnDisplay(closing.id)}
        columns={[
          {
            label: 'Investor',
            fieldName: '_investorNameWithBadge',
            type: 'CUSTOM',
            customValue: (value: any) => value,
          },
          {
            label: 'Entity',
            fieldName: 'investmentEntity.name',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Funding Source',
            fieldName: 'fundingSource',
            type: 'FUNDING_SOURCE',
          },
          {
            label: 'CA Signing',
            fieldName: '_contributionAgreementSigningStatus',
            type: 'DOCUMENT_SIGNING_STATUS',
          },
          {
            label: 'Amount',
            fieldName: 'amount',
            type: 'CURRENCY',
          },
          {
            label: 'Current Value',
            fieldName: 'currentValue',
            type: 'CURRENCY',
          },
          {
            label: 'Realized Value',
            fieldName: 'realizedValue',
            type: 'CURRENCY',
          },
          {
            label: 'Ownership (%)',
            fieldName: 'ownershipPercentage',
            type: 'PERCENTAGE',
          },
          {
            label: 'AL Carry',
            fieldName: 'alCarryPercentage',
            type: 'PERCENTAGE',
          },
          {
            label: 'GP Carry',
            fieldName: 'gpCarryPercentage',
            type: 'PERCENTAGE',
          },
          {
            label: 'Closing Date',
            fieldName: 'createdAt',
            type: 'DATE',
          },
          {
            label: 'Source deal invite link',
            fieldName: 'sourceDealInviteLink.id',
            type: 'STRING',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
      {data.deal.syndicate.type === 'FUND' ? (
        <div className="mt-4">
          <h1 className="text-lg font-bold mb-4">Deal Participants</h1>

          {closings.map(closing => (
            <div key={closing.id}>
              <DataTable
                data={closing.fundDealClosingParticipants || []}
                columns={[
                  {
                    label: 'Investment Entity',
                    fieldName: 'investmentEntity.name',
                  },
                  {
                    label: 'Investor',
                    fieldName: 'investmentEntity.user.name',
                  },
                  {
                    label: 'Amount Invested',
                    fieldName: 'amountInvested',
                    type: 'CURRENCY',
                  },
                  {
                    label: 'Ownership Percentage',
                    fieldName: 'ownershipPercentage',
                    type: 'PERCENTAGE_WITH_DECIMAL',
                  },
                  {
                    label: 'Status',
                    fieldName: 'status',
                    type: 'STATUS',
                  },
                ]}
              />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
};

export default DealClosings;
